import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, AlertTriangle, LogOut, MessageSquare, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { fmtTimeAgo } from '@/lib/realtime';

export default function FeedbackAdminPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([
        api.get('/feedback'),
        api.get('/feedback/summary'),
      ]);
      setFeedback(f.data || []);
      setSummary(s.data);
      try {
        const a = await api.get('/feedback/alerts');
        setAlerts(a.data?.alerts || []);
      } catch (e) {
        // Cashier may lack permission in older deployments — silent fallback
        setAlerts([]);
      }
    } catch (e) { toast.error('Failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 sm:px-6">
        <div>
          <h1 className="font-display text-lg font-semibold">Customer feedback</h1>
          <p className="text-xs text-muted-foreground">Average ratings & low-rating alerts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="feedback-refresh-button" size="sm" variant="secondary" onClick={load}><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
          <Button size="sm" variant="secondary" onClick={() => nav('/server')}>Server</Button>
          <Button size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6">
        {alerts.length > 0 && (
          <Card className="border-rose-300 bg-rose-50">
            <CardHeader><CardTitle className="text-rose-900"><AlertTriangle className="mr-2 inline h-5 w-5" /> Low-rating alerts ({alerts.length})</CardTitle></CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {alerts.map((a) => (
                  <li key={a.id} data-testid="feedback-alert-row" className="rounded border border-rose-200 bg-white p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-rose-900">{a.user_name}</span>
                      <Badge className="border border-rose-300 bg-rose-100 text-rose-900">Avg {a.avg_rating}/5</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{fmtTimeAgo(a.created_at)}</p>
                    {a.comment && <p className="mt-1 italic">“{a.comment}”</p>}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
        <div className="grid gap-3 sm:grid-cols-4">
          <Sum label="Food" value={summary?.avg_food ?? 0} />
          <Sum label="Service" value={summary?.avg_service ?? 0} />
          <Sum label="Cleanliness" value={summary?.avg_cleanliness ?? 0} />
          <Sum label="Delivery" value={summary?.avg_delivery ?? 0} />
        </div>
        <Card>
          <CardHeader><CardTitle>All feedback ({feedback.length})</CardTitle></CardHeader>
          <CardContent>
            {!feedback.length && <p className="text-sm text-muted-foreground">No feedback yet.</p>}
            <ul className="divide-y divide-border">
              {feedback.map((f) => (
                <li key={f.id} data-testid="feedback-row" className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">{f.user_name}</p>
                      <p className="text-xs text-muted-foreground">{fmtTimeAgo(f.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Stars n={f.avg_rating} />
                      <span className="ml-2 text-sm tabular text-muted-foreground">{f.avg_rating}/5</span>
                    </div>
                  </div>
                  {f.comment && <p className="mt-2 text-sm italic"><MessageSquare className="mr-1 inline h-3.5 w-3.5" /> “{f.comment}”</p>}
                  <div className="mt-1 grid grid-cols-4 gap-2 text-xs text-muted-foreground">
                    <span>Food: {f.food_rating}/5</span>
                    <span>Service: {f.service_rating}/5</span>
                    <span>Clean: {f.cleanliness_rating}/5</span>
                    <span>Delivery: {f.delivery_rating ?? '—'}</span>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
function Sum({ label, value }) {
  return (
    <Card><CardContent className="p-4 text-center">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-2xl font-semibold tabular text-primary">{Number(value).toFixed(2)}</p>
    </CardContent></Card>
  );
}
function Stars({ n }) {
  const count = Math.max(0, Math.round(n || 0));
  return (<span className="inline-flex items-center">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= count ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground/40'}`} />)}</span>);
}
